import Echo from "laravel-echo";
import Pusher from "pusher-js";

// Laravel Reverb uses the Pusher protocol
declare global {
    interface Window {
        Echo: any;
        Pusher: any;
    }
}

// Make Pusher available globally for Echo
window.Pusher = Pusher;

/**
 * Initialize Laravel Echo for real-time WebSocket connections
 * Using Laravel Reverb with Pusher protocol
 */
export function initializeEcho() {
    if (typeof window !== "undefined" && !window.Echo) {
        // Get WebSocket configuration from meta tags or environment
        const reverbKey =
            document
                .querySelector('meta[name="reverb-key"]')
                ?.getAttribute("content") ||
            import.meta.env.VITE_REVERB_APP_KEY;
        const reverbHost =
            document
                .querySelector('meta[name="reverb-host"]')
                ?.getAttribute("content") ||
            import.meta.env.VITE_REVERB_HOST ||
            window.location.hostname;
        const reverbPort = Number(
            document
                .querySelector('meta[name="reverb-port"]')
                ?.getAttribute("content") ||
                import.meta.env.VITE_REVERB_PORT ||
                (window.location.protocol === "https:" ? 443 : 80),
        );
        const reverbScheme =
            document
                .querySelector('meta[name="reverb-scheme"]')
                ?.getAttribute("content") ||
            import.meta.env.VITE_REVERB_SCHEME ||
            (window.location.protocol === "https:" ? "https" : "http");

        if (!reverbKey) {
            console.warn(
                "Reverb key not found. WebSocket features will be disabled.",
            );
            return null;
        }

        try {
            window.Echo = new Echo({
                broadcaster: "reverb",
                key: reverbKey,
                wsHost: reverbHost,
                wsPort: reverbPort,
                wssPort: reverbPort,
                forceTLS: reverbScheme === "https",
                enabledTransports: ["ws", "wss"],
                authEndpoint: "/broadcasting/auth",
                auth: {
                    headers: {
                        "X-Participant-UUID": JSON.parse(localStorage.getItem("quiz_session") || "{}").participantUuid || "",
                        "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || "",
                        Accept: "application/json",
                    },
                },
            });

            // Add connection event listeners
            const connection = window.Echo.connector?.pusher?.connection;
            if (connection) {
                connection.bind("connected", () => {
                    console.log("WebSocket connected successfully");
                });

                connection.bind("disconnected", () => {
                    console.warn("WebSocket disconnected");
                });

                connection.bind("error", (error: any) => {
                    console.error("WebSocket connection error:", error);
                });
            }

            return window.Echo;
        } catch (error) {
            console.error("Failed to initialize Laravel Echo:", error);
            return null;
        }
    }

    return window.Echo;
}

/**
 * Get WebSocket connection status
 */
export function getConnectionStatus():
    | "connected"
    | "disconnected"
    | "connecting"
    | "error" {
    if (!window.Echo) {
        return "disconnected";
    }

    const connection = window.Echo.connector?.pusher?.connection;

    if (!connection) {
        return "disconnected";
    }

    if (connection.state === "connected") {
        return "connected";
    } else if (connection.state === "connecting") {
        return "connecting";
    } else if (connection.state === "failed") {
        return "error";
    }

    return "disconnected";
}

/**
 * Attempt to reconnect WebSocket
 */
export function reconnectWebSocket(): boolean {
    if (!window.Echo) {
        return false;
    }

    const connection = window.Echo.connector?.pusher?.connection;
    if (!connection) {
        console.error("WebSocket connection not available");
        return false;
    }

    try {
        connection.disconnect();
        connection.connect();
        return true;
    } catch (error) {
        console.error("Failed to reconnect WebSocket:", error);
        return false;
    }
}

/**
 * Setup automatic reconnection with exponential backoff
 */
export function setupAutoReconnect(maxRetries: number = 5) {
    let retryCount = 0;
    let reconnectTimeout: NodeJS.Timeout;

    const attemptReconnect = () => {
        const status = getConnectionStatus();

        if (status === "connected") {
            retryCount = 0;
            return;
        }

        if (retryCount >= maxRetries) {
            console.error(
                "Max reconnection attempts reached. Please refresh the page.",
            );
            return;
        }

        const delay = Math.min(1000 * 2 ** retryCount, 30000); // Exponential backoff, max 30s

        console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`,
        );

        reconnectTimeout = setTimeout(() => {
            retryCount++;
            if (reconnectWebSocket()) {
                retryCount = 0;
            } else {
                attemptReconnect();
            }
        }, delay);
    };

    // Listen for connection events
    if (window.Echo) {
        const connection = window.Echo.connector?.pusher?.connection;
        if (connection) {
            connection.bind("disconnected", attemptReconnect);
            connection.bind("error", attemptReconnect);
        }
    }
}

export default {
    initializeEcho,
    getConnectionStatus,
    reconnectWebSocket,
    setupAutoReconnect,
};